import { z } from 'zod';
import { EQUIPMENT_CONDITION, PaymentType } from '@trnsact/trnsact-shared-types';
import { DepartmentSchema } from './department';

export const ChecklistItemSchema = z.object({
  name: z.string(),
  isRequired: z.boolean(),
  value: z.boolean().optional(),
  id: z.string().uuid().optional(),
});

export const ChecklistAttributeSchema = z.object({
  field: z.literal('checklist'),
  value: z.array(ChecklistItemSchema),
});

export const DocumentItemSchema = z.object({
  name: z.string(),
  value: z.boolean(),
  isPII: z.boolean(),
  id: z.string().uuid(),
  isRequired: z.boolean(),
  notify: z.array(z.string().uuid()),
  availableForRoles: z.array(z.string()),
});

export const DocumentsAttributeSchema = z.object({
  field: z.literal('document'),
  value: z.array(DocumentItemSchema),
});

export const AllAttributesTypesSchema = z.discriminatedUnion('field', [
  ChecklistAttributeSchema,
  DocumentsAttributeSchema,
]);

export const fieldValuesSchema = z.object({
  id: z.string().uuid(),
  isCompleted: z.boolean(),
  completedDateTime: z.string().nullable(),
  values: z.array(AllAttributesTypesSchema).nullable(),
  completedBy: z
    .object({
      fullName: z.string(),
      id: z.string().uuid(),
    })
    .nullable(),
});

export const StageSchema = z.object({
  name: z.string(),
  ordinal: z.number(),
  winProbability: z.number(),
  completionRules: z.string(),
  fieldValues: fieldValuesSchema,
  businessProcessFlowId: z.string().uuid(),
  businessProcessFlowStageId: z.string().uuid(),
  stageFields: z.array(AllAttributesTypesSchema),
  meta: z.object({
    isLast: z.boolean(),
    isFirst: z.boolean(),
    isPreLast: z.boolean(),
  }),
  notificationRules: z.array(
    z.object({
      notificationEvents: z.array(z.string()),
      roleRecipients: z.array(
        z.object({
          role: z.string(),
        })
      ),
      userProfileRecipients: z.array(
        z.object({
          userProfileId: z.string().uuid(),
        })
      ),
    })
  ),
});

export const CreateOrUpdateStageSchema = z.object({
  name: z.string(),
  ordinal: z.number(),
  completionRules: z.any(),
  notificationRules: z.any(),
  winProbability: z.number().nullable(),
  id: z.string().uuid().nullable().optional(),
  stageFields: z.array(AllAttributesTypesSchema),
});

export const BPFApplyRulesSchema = z.object({
  paymentType: z.union([z.literal(''), z.nativeEnum(PaymentType)]).optional(),
  applyingFor: z.string().optional(),
  equipmentCondition: z.union([z.literal(''), z.nativeEnum(EQUIPMENT_CONDITION)]).optional(),
});

export const TemplateSchema = z.object({
  name: z.string(),
  isActive: z.boolean(),
  createdBy: z.string(),
  createdDateTime: z.string(),
  accountId: z.string().uuid(),
  stages: z.array(StageSchema),
  department: DepartmentSchema,
  applyRules: BPFApplyRulesSchema,
  modifiedDateTime: z.string().nullable(),
  businessProcessFlowId: z.string().uuid(),
});

export const CreateBPFTemplateSchema = z.object({
  isActive: z.boolean(),
  accountId: z.string().uuid(),
  departmentId: z.string().uuid(),
  applyRules: BPFApplyRulesSchema,
  name: z.string().min(1),
  stages: z.array(CreateOrUpdateStageSchema),
});

export const CopyBPFTemplateSchema = z.object({
  sourceTemplateId: z.string().uuid(),
  newTemplateDto: CreateBPFTemplateSchema,
});

export const UpdateBPFTemplateSchema = z
  .object({
    businessProcessFlowId: z.string().uuid(),
  })
  .and(CreateBPFTemplateSchema);

export const UpdateStageFieldsSchema = z.object({
  parentId: z.string().uuid(),
  parentType: z.string(),
  stageId: z.string().uuid(),
  stageFields: z.array(AllAttributesTypesSchema),
});
